import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Box,
    Button,
    HStack,
    Link,
    VStack,
    Text,
    Table,
    Spinner,
    IconButton,
    SimpleGrid,
    Menu,
} from "@chakra-ui/react";
import { HiOutlineCpuChip, HiOutlineClipboard, HiArrowDownTray, HiStar } from "react-icons/hi2";
import useSetPageHeader from "@/components/hooks/useSetPageHeader";
import { ConversationService } from "@/services/ConversationService";
import { toaster } from "@/components/ui/toaster";
import dayjs from "dayjs";
import InfoField from "./components/InfoField";
import ChannelBadge from "./components/ChannelBadge";
import InteractionStatusBadge from "./components/InteractionStatusBadge";
import AnswerValue from "./components/AnswerValue";

const MANUAL_STATUSES = [
    "QualifiedForNextStage",
    "FurtherSelection",
    "ReadyToHire",
    "Hired",
    "Rejected",
    "CandidateResigned",
];

const extractPhone = (conv) => {
    if (conv.whatsAppId) return conv.whatsAppId;
    if (!conv.collectedSlots) return null;
    try {
        const slots = typeof conv.collectedSlots === 'string' ? JSON.parse(conv.collectedSlots) : conv.collectedSlots;
        return slots.phoneNumber ?? slots.phone ?? null;
    } catch {}
    return null;
};

const ConversationDetailPage = () => {
    const { t } = useTranslation("models");
    const { id, recruitmentId: recruitmentIdParam, campaignId, conversationId } = useParams();
    const recruitmentId = recruitmentIdParam ?? campaignId;

    const [conversation, setConversation] = useState(null);
    const [attachments, setAttachments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isXlsxLoading, setIsXlsxLoading] = useState(false);
    const [isFavLoading, setIsFavLoading] = useState(false);
    const [isStatusLoading, setIsStatusLoading] = useState(false);

    useSetPageHeader(t("conversationDetailPage.headerTitle"), "", <HiOutlineCpuChip />);

    useEffect(() => {
        setIsLoading(true);
            ConversationService.getConversationById(id, recruitmentId, conversationId)
                .then((conversationData) => {
            if (conversationData) {
                let entities = {};
                try { entities = JSON.parse(conversationData.collectedSlots ?? '{}'); } catch {}
                let displayAnswers = [];
                let displayQA = [];
                let displayDocs = [];
                try {
                    const answers = JSON.parse(conversationData.collectedAnswers ?? '[]');
                    const sorted = [...answers].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

                    const PINNED_TOP = ["@fullName", "@email", "@phoneNumber", "@language"];
                    displayAnswers = sorted
                        .filter(a => (a.display_name || a.name === "@document_summary") && a.value !== "__FORM_ACTION_RESUMED__" && a.value !== "uploaded")
                        .sort((a, b) => {
                            if (a.name === "@document_summary") return 1;
                            if (b.name === "@document_summary") return -1;
                            const ai = PINNED_TOP.indexOf(a.name);
                            const bi = PINNED_TOP.indexOf(b.name);
                            if (ai !== -1 || bi !== -1) {
                                if (ai === -1) return 1;
                                if (bi === -1) return -1;
                                return ai - bi;
                            }
                            return (a.order ?? 0) - (b.order ?? 0);
                        });

                    const qCompleteIdx = sorted.findIndex(a => a.name === "@questionsComplete");
                    const dCompleteIdx = sorted.findIndex(a => a.name === "@documentsComplete");
                    const qEnd = qCompleteIdx !== -1 ? qCompleteIdx : sorted.length;

                    displayQA = sorted
                        .slice(0, qEnd)
                        .filter(a => !a.display_name && !a.name.startsWith("@"));

                    if (qCompleteIdx !== -1 && dCompleteIdx !== -1) {
                        displayDocs = sorted
                            .slice(qCompleteIdx + 1, dCompleteIdx)
                            .filter(a => !a.display_name && !a.name.startsWith("@"));
                    }
                } catch {}
                setConversation({ ...conversationData, entities, displayAnswers, displayQA, displayDocs });
                setAttachments(conversationData.files ?? []);
            }
            setIsLoading(false);
        });
    }, [id, recruitmentId, conversationId]);

    const handleCopyJson = () => {
        navigator.clipboard.writeText(JSON.stringify(conversation.entities, null, 2));
    };

    const handleDownloadJson = () => {
        const blob = new Blob([JSON.stringify(conversation.entities, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${t("conversationDetailPage.jsonFileNamePrefix")}_${conversationId}_${t("conversationDetailPage.jsonFileNameSuffix")}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const BROWSER_VIEWABLE = /^(application\/pdf|image\/|text\/)/;

    const handleDownloadFile = async (attachment) => {
        const response = await ConversationService.downloadAttachmentFile(conversationId, attachment.name.split('/').slice(1));
        const contentType = response.headers['content-type'] ?? '';
        const blob = new Blob([response.data], { type: contentType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        if (BROWSER_VIEWABLE.test(contentType)) {
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
        } else {
            a.download = attachment.name.split('_').slice(1).join('_');
        }
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 10_000);
    };

    const handleDownloadXlsx = async () => {
        setIsXlsxLoading(true);
        try {
            const response = await ConversationService.exportWorkflowCsv(recruitmentId, [conversationId]);
            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `konwersacja_${conversationId}_${dayjs().format("YYYY-MM-DD")}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } finally {
            setIsXlsxLoading(false);
        }
    };

    const handleToggleFavorite = async () => {
        setIsFavLoading(true);
        const updated = await ConversationService.toggleFavorite(conversationId);
        setConversation((prev) => ({ ...prev, isFavorite: updated.isFavorite }));
        setIsFavLoading(false);
    };

    const handleUpdateStatus = async (newStatus) => {
        setIsStatusLoading(true);
        try {
            const updated = await ConversationService.updateStatus(conversationId, newStatus);
            setConversation(prev => ({ ...prev, status: updated.status }));
            toaster.create({ type: "success", title: t("conversationDetailPage.statusUpdateSuccess") });
        } catch {
            toaster.create({ type: "error", title: t("conversationDetailPage.statusUpdateError") });
        } finally {
            setIsStatusLoading(false);
        }
    };

    const handleDownloadZip = async () => {
        const response = await ConversationService.downloadAllAttachments(conversationId);
        const blob = new Blob([response.data], { type: 'application/zip' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `interaction_${conversationId}.zip`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (isLoading) {
        return (
            <VStack w="full" py={12} align="center">
                <Spinner color="purple.500" size="lg" />
            </VStack>
        );
    }

    if (!conversation) {
        return (
            <VStack w="full" py={12} align="center">
                <Text color="gray.500">{t("conversationDetailPage.notFound")}</Text>
            </VStack>
        );
    }

    return (
        <VStack w="full" py={6} gap={6} align="flex-start">
            <Box w="full" bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} p={5}>
                {conversation.flow && (
                    <SimpleGrid columns={{ base: 2, md: 4 }} gap={4} mb={4}>
                        <InfoField label={t("conversationDetailPage.flow.jobTitle")}>
                            <Text color="gray.900" fontSize="sm" fontWeight={700}>{conversation.flow.jobTitle}</Text>
                        </InfoField>
                        <InfoField label={t("conversationDetailPage.flow.companyName")}>
                            <Text color="gray.900" fontSize="sm">{conversation.flow.companyName}</Text>
                        </InfoField>
                        <InfoField label={t("conversationDetailPage.flow.department")}>
                            <Text color="gray.900" fontSize="sm">{conversation.flow.department}</Text>
                        </InfoField>
                        <InfoField label={t("conversationDetailPage.flow.location")}>
                            <Text color="gray.900" fontSize="sm">{conversation.flow.location}</Text>
                        </InfoField>
                    </SimpleGrid>
                )}
                <InfoField label={t("conversationDetailPage.conversationId")}>
                    <Text color="purple.400" fontSize="sm" fontFamily="mono" wordBreak="break-all">
                        {conversationId}
                    </Text>
                </InfoField>
            </Box>

            {/* Metadata section */}
            <Box w="full" bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} p={5}>
                <SimpleGrid columns={{ base: 2, md: 4 }} gap={4} mb={4}>
                    <InfoField label={t("conversationDetailPage.fieldStartedAt")}>
                        <Text color="gray.900" fontSize="sm">
                            {dayjs(conversation.createdAt).format("DD.MM.YYYY HH:mm")}
                        </Text>
                    </InfoField>
                    <InfoField label={t("conversationDetailPage.fieldLastMessage")}>
                        <Text color="gray.900" fontSize="sm">
                            {dayjs(conversation.updatedAt).format("DD.MM.YYYY HH:mm")}
                        </Text>
                    </InfoField>
                    <InfoField label={t("conversationDetailPage.fieldPhoneNumber")}>
                        {extractPhone(conversation)
                            ? <Link href={`tel:${extractPhone(conversation)}`} color="gray.900" fontSize="sm">{extractPhone(conversation)}</Link>
                            : <Text color="gray.900" fontSize="sm">-</Text>}
                    </InfoField>
                    <InfoField label={t("conversationDetailPage.fieldChannel")}>
                        <ChannelBadge channel={conversation.whatsAppId !== null ? "whatsapp" : "chat"} />
                    </InfoField>
                </SimpleGrid>
                <InfoField label={t("conversationDetailPage.fieldStatus")}>
                    <Menu.Root>
                        <Menu.Trigger asChild disabled={isStatusLoading}>
                            <Box
                                as="button"
                                cursor={isStatusLoading ? "not-allowed" : "pointer"}
                                opacity={isStatusLoading ? 0.5 : 1}
                                _hover={{ opacity: isStatusLoading ? 0.5 : 0.8 }}
                                transition="opacity 0.15s"
                            >
                                <InteractionStatusBadge status={conversation.status} />
                            </Box>
                        </Menu.Trigger>
                        <Menu.Positioner>
                            <Menu.Content bg="white" borderColor="gray.200">
                                {MANUAL_STATUSES.map(s => (
                                    <Menu.Item
                                        key={s}
                                        value={s}
                                        color="gray.900"
                                        _hover={{ bg: "gray.50" }}
                                        onClick={() => handleUpdateStatus(s)}
                                    >
                                        {t(`conversationStatus.${s.toLowerCase()}`)}
                                    </Menu.Item>
                                ))}
                            </Menu.Content>
                        </Menu.Positioner>
                    </Menu.Root>
                </InfoField>
            </Box>

            {conversation.displayAnswers?.length > 0 && (
                <VStack w="full" align="flex-start" gap={3}>
                    <HStack w="full" justify="space-between">
                        <Text fontWeight={700} fontSize="lg" color="gray.900">
                            {t("conversationDetailPage.answersTitle")}
                        </Text>
                        <HStack gap={2}>
                            <Button variant="outlined" size="sm" onClick={handleDownloadXlsx} loading={isXlsxLoading}>
                                {t("conversationDetailPage.downloadXlsx")}
                            </Button>
                            <IconButton
                                variant="ghost"
                                size="sm"
                                aria-label="Ulubione"
                                color={conversation.isFavorite ? "yellow.400" : "gray.500"}
                                loading={isFavLoading}
                                onClick={handleToggleFavorite}
                            >
                                <HiStar />
                            </IconButton>
                            <IconButton
                                variant="ghost"
                                size="sm"
                                aria-label={t("conversationDetailPage.copyJson")}
                                onClick={handleCopyJson}
                                color="gray.500"
                                _hover={{ color: "gray.900" }}
                            >
                                <HiOutlineClipboard />
                            </IconButton>
                            <IconButton
                                variant="ghost"
                                size="sm"
                                aria-label={t("conversationDetailPage.downloadJson")}
                                onClick={handleDownloadJson}
                                color="gray.500"
                                _hover={{ color: "gray.900" }}
                            >
                                <HiArrowDownTray />
                            </IconButton>
                        </HStack>
                    </HStack>
                    <Box w="full" bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} p={5}>
                        <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                            {conversation.displayAnswers.filter(a => a.name !== "@document_summary").map((answer) => (
                                <InfoField key={answer.name} label={answer.display_name}>
                                    {answer.name === "@phoneNumber"
                                        ? <Link href={`tel:${answer.value}`} color="gray.900" fontSize="sm">{answer.value}</Link>
                                        : <Text color="gray.900" fontSize="sm">{answer.value}</Text>}
                                </InfoField>
                            ))}
                        </SimpleGrid>
                        {conversation.displayAnswers.some(a => a.name === "@document_summary") && (
                            <Box mt={4}>
                                <InfoField label={t("conversationDetailPage.documentSummaryLabel")}>
                                    <Text color="gray.900" fontSize="sm">
                                        {conversation.displayAnswers.find(a => a.name === "@document_summary")?.value}
                                    </Text>
                                </InfoField>
                            </Box>
                        )}
                    </Box>
                </VStack>
            )}

            {conversation.displayQA?.length > 0 && (
                <VStack w="full" align="flex-start" gap={3}>
                    <Text fontWeight={700} fontSize="lg" color="gray.900">
                        {t("conversationDetailPage.qaTitle")}
                    </Text>
                    <Box w="full" bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} overflow="hidden">
                        {conversation.displayQA.map((item, i) => (
                            <Box
                                key={item.name}
                                px={5}
                                py={4}
                                borderBottom={i < conversation.displayQA.length - 1 ? "1px solid" : "none"}
                                borderColor="gray.200"
                            >
                                <Text color="gray.500" fontSize="sm" mb={1}>{item.name}</Text>
                                <AnswerValue item={item} />
                            </Box>
                        ))}
                    </Box>
                </VStack>
            )}

            {conversation.displayDocs?.length > 0 && (
                <VStack w="full" align="flex-start" gap={3}>
                    <Text fontWeight={700} fontSize="lg" color="gray.900">
                        {t("conversationDetailPage.docsTitle")}
                    </Text>
                    <Box w="full" bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} overflow="hidden">
                        {conversation.displayDocs.map((item, i) => (
                            <Box
                                key={item.name}
                                px={5}
                                py={4}
                                borderBottom={i < conversation.displayDocs.length - 1 ? "1px solid" : "none"}
                                borderColor="gray.200"
                            >
                                <Text color="gray.500" fontSize="sm" mb={1}>{item.name}</Text>
                                <AnswerValue item={item} />
                            </Box>
                        ))}
                    </Box>
                </VStack>
            )}

            <VStack w="full" align="flex-start" gap={3}>
                <HStack w="full" justify="space-between">
                    <Text fontWeight={700} fontSize="lg" color="gray.900">
                        {t("conversationDetailPage.attachmentsTitle")}
                    </Text>
                    {attachments.length > 0 && (
                        <Button
                            variant="outlined"
                            size="sm"
                            onClick={handleDownloadZip}
                            leftIcon={<HiArrowDownTray />}
                        >
                            {t("conversationDetailPage.downloadAllZip")}
                        </Button>
                    )}
                </HStack>

                <Box
                    w="full"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius={12}
                    overflow="hidden"
                >
                    {attachments.length === 0 ? (
                        <Box bg="white" py={10} textAlign="center">
                            <Text color="gray.500" fontSize="sm">
                                {t("conversationDetailPage.noAttachments")}
                            </Text>
                        </Box>
                    ) : (
                        <>
                            {/* Desktop table */}
                            <Box display={{ base: 'none', md: 'block' }} overflowX="auto">
                                <Table.Root minW="720px">
                                    <Table.Header>
                                        <Table.Row bg="white" borderBottom="1px solid" borderColor="gray.200">
                                            {[
                                                t("conversationDetailPage.columnFileName"),
                                                t("conversationDetailPage.columnType"),
                                                t("conversationDetailPage.columnUploadedAt"),
                                                t("conversationDetailPage.columnSize"),
                                                t("conversationDetailPage.columnActions"),
                                            ].map((col, i) => (
                                                <Table.ColumnHeader
                                                    key={col}
                                                    color="gray.500"
                                                    fontWeight={600}
                                                    fontSize="xs"
                                                    textTransform="uppercase"
                                                    letterSpacing="wider"
                                                    py={3}
                                                    px={5}
                                                    border="none"
                                                    textAlign={i === 4 ? "right" : "left"}
                                                >
                                                    {col}
                                                </Table.ColumnHeader>
                                            ))}
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {attachments.map((file) => (
                                            <Table.Row
                                                key={file.id}
                                                bg="white"
                                                borderBottom="1px solid"
                                                borderColor="gray.200"
                                                _hover={{ bg: "gray.50" }}
                                                transition="background 0.15s"
                                            >
                                                <Table.Cell py={4} px={5} border="none">
                                                    <Text color="gray.900" fontSize="sm" fontFamily="mono">
                                                        {file.name.split('_').slice(1).join('_')}
                                                    </Text>
                                                </Table.Cell>
                                                <Table.Cell py={4} px={5} border="none">
                                                    <Text color="gray.500" fontSize="xs">{file.contentType}</Text>
                                                </Table.Cell>
                                                <Table.Cell py={4} px={5} border="none">
                                                    <Text color="gray.900" fontSize="sm">
                                                        {dayjs(file.createdAt).format("DD.MM.YYYY HH:mm")}
                                                    </Text>
                                                </Table.Cell>
                                                <Table.Cell py={4} px={5} border="none">
                                                    <Text color="gray.900" fontSize="sm">{file.size}</Text>
                                                </Table.Cell>
                                                <Table.Cell py={4} px={5} border="none" textAlign="right">
                                                    <IconButton
                                                        variant="outlined"
                                                        size="sm"
                                                        aria-label={t("conversationDetailPage.downloadFile")}
                                                        onClick={() => handleDownloadFile(file)}
                                                    >
                                                        <HiArrowDownTray />
                                                    </IconButton>
                                                </Table.Cell>
                                            </Table.Row>
                                        ))}
                                    </Table.Body>
                                </Table.Root>
                            </Box>

                            {/* Mobile file list */}
                            <VStack display={{ base: 'flex', md: 'none' }} gap={0} bg="white">
                                {attachments.map((file, i) => (
                                    <HStack
                                        key={file.id}
                                        w="full"
                                        justify="space-between"
                                        px={4}
                                        py={3}
                                        borderBottom={i < attachments.length - 1 ? "1px solid" : "none"}
                                        borderColor="gray.200"
                                    >
                                        <VStack align="flex-start" gap={0} flex={1} minW={0}>
                                            <Text color="gray.900" fontSize="sm" fontFamily="mono" truncate maxW="full">
                                                {file.name.split('_').slice(1).join('_')}
                                            </Text>
                                            <Text color="gray.500" fontSize="xs">
                                                {file.contentType} · {file.size}
                                            </Text>
                                        </VStack>
                                        <IconButton
                                            variant="outlined"
                                            size="sm"
                                            aria-label={t("conversationDetailPage.downloadFile")}
                                            flexShrink={0}
                                            onClick={() => handleDownloadFile(file)}
                                        >
                                            <HiArrowDownTray />
                                        </IconButton>
                                    </HStack>
                                ))}
                            </VStack>
                        </>
                    )}
                </Box>
            </VStack>

        </VStack>
    );
};

export default ConversationDetailPage;
